import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { AuthCodeModal } from '../components/AuthCodeModal';
import { api } from '../lib/api';
import type {
  BalanceSheet,
  CompanyLookupResponse,
  LastAccountsResponse,
  PeriodAccounts,
} from '@dormant/shared';

const STATUTORY_STATEMENTS = [
  'For the year ending {periodEnd}, the company was entitled to exemption from audit under section 480 of the Companies Act 2006 relating to dormant companies.',
  'The members have not required the company to obtain an audit in accordance with section 476 of the Companies Act 2006.',
  'The directors acknowledge their responsibilities for complying with the requirements of the Act with respect to accounting records and the preparation of accounts.',
  "These accounts have been prepared in accordance with the provisions applicable to companies subject to the small companies' regime.",
];

/**
 * Editable line items the user fills in. Values are "positive" amounts as the
 * user types them – creditors are stored positive here and displayed as
 * negatives. We compute Net Assets and Capital & Reserves from these.
 */
interface CyFigures {
  fixedAssets: string;
  currentAssets: string;
  creditorsWithinOneYear: string;
  creditorsAfterOneYear: string;
  calledUpShareCapital: string;
}

const EMPTY_CY: CyFigures = {
  fixedAssets: '',
  currentAssets: '',
  creditorsWithinOneYear: '',
  creditorsAfterOneYear: '',
  calledUpShareCapital: '1',
};

function num(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function formatPeriodEnd(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatYear(iso?: string): string {
  return iso ? iso.slice(0, 4) : '';
}

function fmt(n?: number): string {
  if (n === undefined || n === null) return '-';
  if (n === 0) return '-';
  const abs = Math.abs(n).toLocaleString('en-GB');
  return n < 0 ? `(${abs})` : abs;
}

export function Confirm() {
  const { number } = useParams();
  const navigate = useNavigate();
  const [lookup, setLookup] = useState<CompanyLookupResponse | null>(null);
  const [last, setLast] = useState<LastAccountsResponse | null>(null);
  const [cy, setCy] = useState<CyFigures>(EMPTY_CY);
  // Whether the prior filing used the 'called up share capital not paid'
  // layout instead of the cash variant. We mirror the prior layout in the
  // new filing so the comparative column makes sense.
  const [isUnpaidVariant, setIsUnpaidVariant] = useState(false);
  const [bsAnswer, setBsAnswer] = useState<'yes' | 'no' | null>(null);
  const [dormantAnswer, setDormantAnswer] = useState<'yes' | 'no' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  // Share capital is hidden by default – virtually all UK ltds have £1
  // share capital so there's nothing to edit. Surfaced via an "Advanced"
  // link for the rare edge case (e.g. companies with £100 share capital).
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Blocking modal shown when the prior-year filing indicates trading
  // activity (creditors, retained earnings, fixed assets). AA02 dormant
  // accounts only support share capital + optional cash, so we can't
  // legitimately file dormant for a company that had trading history.
  // Instead of a hard rejection we route the customer to email us for
  // manual assistance with the correct form (AA / FRS 105 micro).
  const [nonDormantPriorYear, setNonDormantPriorYear] = useState<null | {
    creditorsWithinOneYear: number;
    creditorsAfterOneYear: number;
    profitAndLossAccount: number;
    fixedAssets: number;
  }>(null);

  useEffect(() => {
    if (!number) return;
    (async () => {
      try {
        const [l, la] = await Promise.all([
          api.lookupCompany(number),
          api.lastAccounts(number),
        ]);
        setLookup(l);
        setLast(la);

        // Non-dormant prior-year check. AA02 dormant accounts only
        // support share capital + optional cash, so if the customer's
        // most recent filing on the CH register was ANYTHING other
        // than a dormant filing (small accounts, full accounts,
        // micro-entity FRS 105, etc.) we can't legitimately file AA02
        // dormant this year. Route them to email us for the correct
        // form (AA / FRS 105 micro) before they invest time in the
        // wizard.
        if (!la.isFirstFiling && la.lastFilingWasDormant === false) {
          const bs = la.current?.balanceSheet;
          setNonDormantPriorYear({
            creditorsWithinOneYear: Math.abs(bs?.creditorsDueWithinOneYear ?? 0),
            creditorsAfterOneYear: Math.abs(bs?.creditorsDueAfterOneYear ?? 0),
            profitAndLossAccount: bs?.profitAndLossAccount ?? 0,
            fixedAssets: bs?.fixedAssets ?? 0,
          });
        }

        // Pre-fill the editable fields.
        // - Continuation: copy from last filing's CY balance sheet.
        // - First filing: balanced default of £1 share capital + £1 in current
        //   assets (the most common UK ltd shape – 1 ordinary £1 share). User
        //   edits if their setup differs.
        const filed = la.current?.balanceSheet;
        // True first filing (no prior accounts at all) → empty defaults.
        // Otherwise – even if last filing wasn't dormant – we still pre-fill
        // from the extracted figures so the user can review and edit them.
        if (la.isFirstFiling) {
          // First filing default is the UNPAID variant. Most genuinely
          // dormant UK companies never actually deposited their £1 share
          // capital into a bank account — the share was subscribed but
          // never paid in. The customer can flip this via the "Has your
          // share capital been paid in?" toggle rendered on the Confirm
          // page. Toggling to "Yes" sets currentAssets = calledUpShareCapital
          // (cash variant).
          setIsUnpaidVariant(true);
          const sc = String(la.defaultShareCapital ?? 1);
          setCy({
            fixedAssets: '0',
            currentAssets: '0', // unpaid variant = no cash on the asset side
            creditorsWithinOneYear: '0',
            creditorsAfterOneYear: '0',
            calledUpShareCapital: sc,
          });
        } else {
          // Continuation: pre-fill from the last filing. If the previous
          // year used the "called up share capital not paid" variant we
          // keep currentAssets at 0 (which trips the unpaid variant in the
          // iXBRL generator) so the current year matches the customer's
          // historical presentation. Otherwise we mirror the previous
          // year's cash position. Creditors are stored negative in the
          // parsed sheet — flip back to a positive amount for the input.
          const priorWasUnpaid =
            (filed?.calledUpShareCapitalNotPaid ?? 0) > 0 &&
            (filed?.currentAssets ?? 0) === 0;
          setIsUnpaidVariant(priorWasUnpaid);
          setCy({
            fixedAssets: filed?.fixedAssets ? String(filed.fixedAssets) : '',
            currentAssets: priorWasUnpaid
              ? '0'
              : filed?.currentAssets
                ? String(filed.currentAssets)
                : '',
            creditorsWithinOneYear: filed?.creditorsDueWithinOneYear
              ? String(Math.abs(filed.creditorsDueWithinOneYear))
              : '',
            creditorsAfterOneYear: filed?.creditorsDueAfterOneYear
              ? String(Math.abs(filed.creditorsDueAfterOneYear))
              : '',
            calledUpShareCapital: String(
              filed?.calledUpShareCapital ??
                filed?.calledUpShareCapitalNotPaid ??
                la.defaultShareCapital ??
                1,
            ),
          });
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      }
    })();
  }, [number]);

  // Computed totals. For the 'unpaid' variant the iXBRL generator uses
  // the share-capital amount as the asset cell (Called up share capital
  // not paid IS the asset), so the balance check has to count it on
  // both sides.
  const netAssets = useMemo(() => {
    const assetSide = isUnpaidVariant
      ? num(cy.calledUpShareCapital) + num(cy.fixedAssets)
      : num(cy.fixedAssets) + num(cy.currentAssets);
    return (
      assetSide -
      num(cy.creditorsWithinOneYear) -
      num(cy.creditorsAfterOneYear)
    );
  }, [cy, isUnpaidVariant]);
  // P&L is auto-derived to make the sheet balance:
  //   Net Assets = Called-up share capital + Profit & Loss Account
  // For pure-dormant companies that never traded, P&L is 0 (so equity =
  // share capital = current assets). For previously-trading dormants with
  // accumulated profits or losses, P&L absorbs the difference. This is
  // displayed but not editable – the user controls share capital, P&L
  // follows.
  const profitAndLoss = useMemo(
    () => netAssets - num(cy.calledUpShareCapital),
    [netAssets, cy.calledUpShareCapital],
  );
  const capitalAndReserves = useMemo(
    () => num(cy.calledUpShareCapital) + profitAndLoss,
    [cy.calledUpShareCapital, profitAndLoss],
  );

  const canContinue =
    bsAnswer === 'yes' &&
    dormantAnswer === 'yes' &&
    num(cy.calledUpShareCapital) > 0;

  if (error)
    return (
      <WizardLayout step="confirm">
        <div className="card text-red-700">{error}</div>
      </WizardLayout>
    );
  if (!lookup || !last)
    return (
      <WizardLayout step="confirm">
        <div className="card">Loading…</div>
      </WizardLayout>
    );

  const period_end = lookup.nextAccounts?.period_end;

  return (
    <WizardLayout step="confirm">
      <div className="card">
        <h1 className="text-2xl font-semibold text-slate-900">{lookup.company.company_name}</h1>
        <p className="mt-1 text-sm text-slate-500">Company number {lookup.company.company_number}</p>

        {lookup.eligibility.warnings.length > 0 && (
          <div className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            {lookup.eligibility.warnings.join(' ')}
          </div>
        )}

        {lookup.nextAccounts && (
          <dl className="mt-6 grid grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-slate-500">Period start</dt>
              <dd className="font-medium">{formatPeriodEnd(lookup.nextAccounts.period_start)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Period end</dt>
              <dd className="font-medium">{formatPeriodEnd(lookup.nextAccounts.period_end)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Due by</dt>
              <dd className={`font-medium ${lookup.nextAccounts.overdue ? 'text-red-600' : ''}`}>
                {formatPeriodEnd(lookup.nextAccounts.due_on)}
              </dd>
            </div>
            {last.isFirstFiling && (
              <div className="col-span-3 text-xs text-slate-500">
                First accounting period – runs from incorporation date to your first
                accounting reference date (
                {Math.round(
                  (new Date(lookup.nextAccounts.period_end).getTime() -
                    new Date(lookup.nextAccounts.period_start).getTime()) /
                    (1000 * 60 * 60 * 24),
                )}{' '}
                days, typically more than 12 months).
              </div>
            )}
          </dl>
        )}

        {last.warning && (
          <div className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            {last.warning}
          </div>
        )}

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <BalanceSheetCard
            companyName={lookup.company.company_name}
            cy={cy}
            setCy={setCy}
            netAssets={netAssets}
            capitalAndReserves={capitalAndReserves}
            isFirstFiling={last.isFirstFiling}
            isUnpaidVariant={isUnpaidVariant}
            onToggleUnpaidVariant={(nextUnpaid) => {
              setIsUnpaidVariant(nextUnpaid);
              // When flipping to unpaid, zero the cash row. When flipping
              // to paid, mirror the share capital as cash so the sheet
              // stays balanced without the customer having to retype.
              setCy((prev) => ({
                ...prev,
                currentAssets: nextUnpaid ? '0' : prev.calledUpShareCapital || '1',
              }));
            }}
            showAdvanced={showAdvanced}
            onToggleAdvanced={() => setShowAdvanced((s) => !s)}
            // For dormant accounts, the new filing's comparative column equals
            // the last filing's CURRENT year (carry-forward), not its prior year.
            comparative={last.current}
            periodEndIso={period_end}
          />

          <div className="flex flex-col gap-6">
            <ConfirmationsCard
              periodEndPretty={formatPeriodEnd(period_end)}
              bsAnswer={bsAnswer}
              setBsAnswer={setBsAnswer}
              dormantAnswer={dormantAnswer}
              setDormantAnswer={setDormantAnswer}
            />
            <div className="flex justify-end">
              <button
                disabled={!canContinue}
                onClick={() => setShowAuthModal(true)}
                className="btn-primary"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>

      <AuthCodeModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onHave={() =>
          navigate(`/auth-code/${number}`, {
            state: {
              companyName: lookup.company.company_name,
              companyNumber: lookup.company.company_number,
              isFirstFiling: last.isFirstFiling,
              periodStart: lookup.nextAccounts!.period_start,
              periodEnd: period_end,
              figures: {
                fixedAssets: num(cy.fixedAssets),
                currentAssets: num(cy.currentAssets),
                creditorsDueWithinOneYear: -Math.abs(num(cy.creditorsWithinOneYear)),
                creditorsDueAfterOneYear: -Math.abs(num(cy.creditorsAfterOneYear)),
                calledUpShareCapital: num(cy.calledUpShareCapital),
                profitAndLossAccount: profitAndLoss,
                netAssets,
              },
            },
          })
        }
        onNeed={() =>
          window.open(
            'https://www.gov.uk/guidance/company-authentication-codes-for-online-filing',
            '_blank',
          )
        }
      />

      {nonDormantPriorYear && lookup && (
        <NonDormantPriorYearModal
          companyName={lookup.company.company_name}
          companyNumber={lookup.company.company_number}
          figures={nonDormantPriorYear}
        />
      )}
    </WizardLayout>
  );
}

/**
 * Blocking modal shown when the company's most recent filing on the
 * Companies House register indicates trading activity (creditors, P&L,
 * or fixed assets on the balance sheet). AA02 dormant accounts only
 * support share capital + optional cash so we can't correctly file
 * dormant accounts for a previously-trading company through this
 * service - the customer needs full accounts (form AA / FRS 105
 * micro-entity) which our software doesn't yet support.
 *
 * Rather than let the customer proceed and pay for a filing we can't
 * complete accurately, we route them to email us with pre-filled
 * subject + body so we can advise on the correct form.
 */
function NonDormantPriorYearModal({
  companyName,
  companyNumber,
  figures,
}: {
  companyName: string;
  companyNumber: string;
  figures: {
    creditorsWithinOneYear: number;
    creditorsAfterOneYear: number;
    profitAndLossAccount: number;
    fixedAssets: number;
  };
}) {
  const CONTACT_EMAIL = 'contact.dormantfiling@gmail.com';
  const totalCreditors = figures.creditorsWithinOneYear + figures.creditorsAfterOneYear;
  // Route the customer to our in-site contact form with company
  // context in the URL. Avoids the Chrome mailto: dead-tab quirk and
  // means we control the entire post-modal experience (visible form,
  // validation, honeypot, submission via /api/contact).
  const contactUrl =
    `/contact?service=non-dormant-help` +
    `&companyName=${encodeURIComponent(companyName)}` +
    `&companyNumber=${encodeURIComponent(companyNumber)}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="non-dormant-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-7 shadow-2xl ring-1 ring-slate-200">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-2xl shadow-[0_8px_20px_-6px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.4)]">
          <span aria-hidden>⚠</span>
        </div>

        <h2 id="non-dormant-title" className="mt-4 text-lg font-semibold text-slate-900">
          Your last filing wasn't a dormant filing
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Companies House shows that <strong>{companyName}</strong>'s most recent accounts
          weren't filed as dormant. That usually means the company was trading in the
          previous period (small accounts, full accounts, or micro-entity FRS 105).
        </p>

        <ul className="mt-3 space-y-1 rounded-md bg-slate-50 p-3 text-sm text-slate-700 ring-1 ring-inset ring-slate-200">
          {figures.fixedAssets > 0 && (
            <li>Fixed assets: <strong>£{figures.fixedAssets.toLocaleString('en-GB')}</strong></li>
          )}
          {totalCreditors > 0 && (
            <li>Creditors: <strong>£{totalCreditors.toLocaleString('en-GB')}</strong></li>
          )}
          {figures.profitAndLossAccount !== 0 && (
            <li>
              Retained earnings / P&amp;L reserve:{' '}
              <strong>£{figures.profitAndLossAccount.toLocaleString('en-GB')}</strong>
            </li>
          )}
        </ul>

        <p className="mt-4 text-sm leading-relaxed text-slate-600">
          AA02 dormant accounts only support <strong>share capital and optional cash at
          bank</strong> — they can't correctly represent creditors, retained earnings, or
          fixed assets. To make sure the correct set of accounts is filed for your company,
          please email us and our team will advise on the right form (typically full accounts
          or micro-entity accounts, form AA).
        </p>

        <a
          href={contactUrl}
          className="btn-primary mt-6 inline-flex w-full items-center justify-center"
        >
          Continue to enquiry form →
        </a>

        <p className="mt-3 text-center text-xs text-slate-500">
          Or email us directly at{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-brand-700 hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}

function BalanceSheetCard({
  companyName,
  cy,
  setCy,
  netAssets,
  capitalAndReserves,
  isFirstFiling,
  isUnpaidVariant,
  onToggleUnpaidVariant,
  comparative,
  periodEndIso,
  showAdvanced,
  onToggleAdvanced,
}: {
  companyName: string;
  cy: CyFigures;
  setCy: (updater: (prev: CyFigures) => CyFigures) => void;
  netAssets: number;
  capitalAndReserves: number;
  isFirstFiling: boolean;
  isUnpaidVariant: boolean;
  onToggleUnpaidVariant: (unpaid: boolean) => void;
  comparative?: PeriodAccounts;  // last filing's CY – becomes new filing's PY
  periodEndIso?: string;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
}) {
  // Header years: "this year" = next period end, "comparative" = year before.
  const thisYear =
    formatYear(periodEndIso) || formatYear(comparative?.endDate) || '';
  const lastYear = thisYear
    ? String(Number(thisYear) - 1)
    : formatYear(comparative?.endDate) || '';

  // Prior column is read-only – comes from last filing's CY (carry-forward).
  const py: BalanceSheet = isFirstFiling ? {} : comparative?.balanceSheet ?? {};

  const update = (k: keyof CyFigures) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setCy((prev) => ({ ...prev, [k]: v }));
  };

  return (
    <section className="rounded-xl bg-brand-50/50 p-3 ring-1 ring-brand-100 sm:p-4">
      <div className="text-center">
        <h2 className="text-sm font-semibold text-slate-900">{companyName}</h2>
        <p className="text-xs text-slate-600">
          Balance Sheet as at {formatPeriodEnd(periodEndIso) || '–'}
        </p>
      </div>

      {isFirstFiling && (
        <>
          <p className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            First filing – please pick the option below that matches your
            company. For most dormant companies (never traded), the £1 share
            was subscribed but never actually paid in — that's the default.
          </p>
          <div className="mt-3 rounded-lg bg-white p-3 ring-1 ring-slate-200">
            <div className="text-xs font-semibold text-slate-700">
              Has your share capital been paid in?
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              i.e. is the £1 (or whatever your share value is) actually sitting
              in a bank account for the company?
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onToggleUnpaidVariant(true)}
                className={`rounded-md border p-2 text-left text-xs transition-colors ${
                  isUnpaidVariant
                    ? 'border-brand-500 bg-brand-50 text-brand-800 ring-1 ring-brand-500'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="font-semibold">No — never paid in</div>
                <div className="mt-0.5 text-[10px] opacity-80">
                  Shows as "Called up share capital not paid" (most common for
                  dormant companies)
                </div>
              </button>
              <button
                type="button"
                onClick={() => onToggleUnpaidVariant(false)}
                className={`rounded-md border p-2 text-left text-xs transition-colors ${
                  !isUnpaidVariant
                    ? 'border-brand-500 bg-brand-50 text-brand-800 ring-1 ring-brand-500'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="font-semibold">Yes — money is in the bank</div>
                <div className="mt-0.5 text-[10px] opacity-80">
                  Shows as "Cash at bank and in hand"
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      <table className="mt-4 w-full text-sm">
        <colgroup>
          <col />
          <col className="w-[140px]" />
          {!isFirstFiling && <col className="w-[100px]" />}
        </colgroup>
        <thead>
          <tr className="text-brand-700">
            <th></th>
            <th className="pb-2 pr-3 text-right text-xs font-semibold">{thisYear || '–'}</th>
            {!isFirstFiling && (
              <th className="pb-2 pl-4 text-right text-xs font-semibold">{lastYear || '–'}</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          <SectionRow label="ASSETS" cols={isFirstFiling ? 2 : 3} />
          <EditableRow label="Fixed Assets" value={cy.fixedAssets} onChange={update('fixedAssets')} pyValue={py.fixedAssets} showPy={!isFirstFiling} optionalHint />
          {isUnpaidVariant ? (
            // The previous filing used the "called up share capital not paid"
            // layout. We mirror it here — the share-capital amount IS the asset
            // (a receivable from shareholders for issued-but-unpaid shares).
            // Read-only because the value tracks `cy.calledUpShareCapital` —
            // editing it would desync the asset and equity sides.
            <ComputedRow
              label="Called up share capital not paid"
              cy={num(cy.calledUpShareCapital)}
              py={py.calledUpShareCapitalNotPaid}
              showPy={!isFirstFiling}
            />
          ) : (
            <EditableRow
              label="Current Assets"
              value={cy.currentAssets}
              onChange={update('currentAssets')}
              pyValue={py.currentAssets}
              showPy={!isFirstFiling}
            />
          )}

          <SectionRow label="LIABILITIES" cols={isFirstFiling ? 2 : 3} />
          <EditableRow label="Creditors: within 1 year" value={cy.creditorsWithinOneYear} onChange={update('creditorsWithinOneYear')} pyValue={py.creditorsDueWithinOneYear} negative showPy={!isFirstFiling} optionalHint />
          <EditableRow label="Creditors: after 1 year" value={cy.creditorsAfterOneYear} onChange={update('creditorsAfterOneYear')} pyValue={py.creditorsDueAfterOneYear} negative showPy={!isFirstFiling} optionalHint />

          <ComputedRow label="Net Assets" cy={netAssets} py={py.netAssets ?? py.equity} bold showPy={!isFirstFiling} />

          <SectionRow label="EQUITY" cols={isFirstFiling ? 2 : 3} />
          <ComputedRow
            label="Capital and Reserves"
            cy={capitalAndReserves}
            py={py.equity ?? py.netAssets}
            bold
            showPy={!isFirstFiling}
          />
          {showAdvanced && (
            <EditableRow
              label="↳ Called-up share capital"
              value={cy.calledUpShareCapital}
              onChange={update('calledUpShareCapital')}
              pyValue={py.calledUpShareCapital}
              showPy={!isFirstFiling}
              required
            />
          )}
        </tbody>
      </table>

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
        <span>
          Figures in £. Other rows are usually £0 for dormant companies. Tap any value to edit.
        </span>
        <div className="ml-3 flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={onToggleAdvanced}
            title={
              showAdvanced
                ? 'Hide the share-capital editor'
                : 'Click if your company has more than one share, or shares of a value other than £1. Most UK companies have a single £1 share.'
            }
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-brand-700 underline underline-offset-2 hover:bg-brand-50 hover:text-brand-800 ${
              showAdvanced ? '' : 'ring-1 ring-brand-200'
            }`}
          >
            <span aria-hidden>✏️</span>
            {showAdvanced ? 'Hide share capital editor' : 'Edit share capital'}
          </button>
          {!showAdvanced && (
            <span
              className="max-w-[240px] text-right text-[11px] leading-tight text-slate-500 sm:hidden"
              aria-hidden
            >
              Only needed if you have more than 1 share or a share value
              other than £1.
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function EditableRow({
  label,
  value,
  onChange,
  pyValue,
  negative = false,
  allowNegative = false,
  showPy = true,
  required = false,
  optionalHint = false,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  pyValue?: number;
  negative?: boolean;
  allowNegative?: boolean;
  showPy?: boolean;
  required?: boolean;
  optionalHint?: boolean;
}) {
  const cyDisplay = num(value);
  return (
    <tr className="group text-slate-700">
      {/* Label allowed to wrap on phones – was `whitespace-nowrap` which
          pushed the whole table past the card edge. Hint sits on its own
          line on phones so the row stays compact. */}
      <td className="py-1.5 pr-2 leading-tight">
        <span className="block">
          {label}
          {required && <span className="text-amber-600"> *</span>}
        </span>
        {optionalHint && (
          <span className="block text-[10px] text-slate-400 sm:inline sm:ml-1 sm:text-[11px]">
            (usually £0)
          </span>
        )}
      </td>
      <td className="py-1 pr-2 text-right sm:pr-3">
        <div className="flex items-center justify-end gap-0">
          {negative && cyDisplay !== 0 && <span className="text-slate-400">(</span>}
          <input
            type="number"
            inputMode="numeric"
            min={allowNegative ? undefined : 0}
            step={1}
            value={value}
            onChange={onChange}
            placeholder="0"
            // Set width per-render based on how many characters the value
            // takes – keeps `(` and `)` flush against the number. Add 2ch
            // of buffer to cover the input's own padding/border and any
            // browser-rendered spinner buttons so digits aren't clipped.
            style={{
              width: `calc(${Math.max(2, Math.min(10, value.length || 1))}ch + 1.25rem)`,
            }}
            className="rounded-md border border-transparent bg-transparent px-1 py-0.5 text-right tabular-nums hover:border-slate-300 hover:bg-white focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 sm:px-1.5"
          />
          {negative && cyDisplay !== 0 && <span className="text-slate-400">)</span>}
        </div>
      </td>
      {showPy && (
        <td className="py-1.5 pl-2 text-right tabular-nums text-slate-500 sm:pl-4">{fmt(pyValue)}</td>
      )}
    </tr>
  );
}

function ComputedRow({
  label,
  cy,
  py,
  bold = false,
  warn = false,
  showPy = true,
}: {
  label: string;
  cy: number;
  py?: number;
  bold?: boolean;
  warn?: boolean;
  showPy?: boolean;
}) {
  return (
    <tr className={bold ? 'font-semibold' : ''}>
      {/* Wrap on phones – the bold "Net Assets" / "Capital and Reserves"
          labels are short enough that wrapping is rare anyway, but
          whitespace-nowrap was forcing the row beyond the card edge. */}
      <td className={`py-2 pr-2 leading-tight ${bold ? 'text-brand-700' : 'text-slate-700'}`}>{label}</td>
      <td
        className={`py-2 pr-2 pl-1 text-right tabular-nums sm:pr-3 ${
          warn ? 'text-amber-700' : bold ? 'text-brand-700' : 'text-slate-700'
        }`}
      >
        {fmt(cy)}
      </td>
      {showPy && (
        <td
          className={`py-2 pl-2 text-right tabular-nums sm:pl-4 ${
            bold ? 'text-brand-700' : 'text-slate-500'
          }`}
        >
          {fmt(py)}
        </td>
      )}
    </tr>
  );
}

function SectionRow({ label, cols = 3 }: { label: string; cols?: number }) {
  return (
    <tr>
      <td colSpan={cols} className="pt-3 pb-1 text-xs font-bold tracking-wide text-slate-900">
        {label}
      </td>
    </tr>
  );
}

function ConfirmationsCard({
  periodEndPretty,
  bsAnswer,
  setBsAnswer,
  dormantAnswer,
  setDormantAnswer,
}: {
  periodEndPretty: string;
  bsAnswer: 'yes' | 'no' | null;
  setBsAnswer: (a: 'yes' | 'no' | null) => void;
  dormantAnswer: 'yes' | 'no' | null;
  setDormantAnswer: (a: 'yes' | 'no' | null) => void;
}) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-slate-900">Statement Confirmation</h2>
      <ul className="mt-3 space-y-2 text-sm text-slate-700">
        {STATUTORY_STATEMENTS.map((s) => (
          <li key={s} className="flex gap-2">
            <span className="text-brand-600">•</span>
            <span>{s.replace('{periodEnd}', periodEndPretty || 'the period')}</span>
          </li>
        ))}
      </ul>

      <div className="mt-5">
        <p className="text-sm font-semibold text-slate-900">
          Do you, as the director of the company, confirm that the balance sheet displayed is
          accurate and complete to the best of your knowledge?
        </p>
        <YesNo value={bsAnswer} onChange={setBsAnswer} />
      </div>

      <h2 className="mt-8 text-lg font-semibold text-slate-900">Dormancy Confirmation</h2>
      <p className="mt-3 text-sm font-semibold text-slate-900">
        Do you confirm that the company has been dormant for the full financial year?
      </p>
      <YesNo value={dormantAnswer} onChange={setDormantAnswer} />
      {dormantAnswer === 'no' && (
        <div className="mt-3 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
          If the company had any significant accounting transactions during the year, it is not
          dormant. Full accounts are required – this service cannot help you in that case.
        </div>
      )}
    </section>
  );
}

function YesNo({
  value,
  onChange,
}: {
  value: 'yes' | 'no' | null;
  onChange: (v: 'yes' | 'no' | null) => void;
}) {
  return (
    <div className="mt-3 flex gap-3">
      {(['yes', 'no'] as const).map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`min-w-[88px] rounded-lg border px-5 py-2 text-sm font-medium transition-colors ${
              selected
                ? opt === 'yes'
                  ? 'border-green-600 bg-green-50 text-green-700'
                  : 'border-red-500 bg-red-50 text-red-700'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {opt === 'yes' ? 'Yes' : 'No'}
          </button>
        );
      })}
    </div>
  );
}
